/**
 * Preferencias de UI puramente cosméticas (qué sub-vista eligió el usuario la
 * última vez), persistidas en `localStorage` para sobrevivir a una navegación
 * fuera del módulo y de vuelta — nada sensible, a diferencia de la sesión
 * (ver el comentario en `servicios/api.ts` sobre por qué esa NO vive acá).
 *
 * Caso que motivó esto (§ bug reportado): el enlace «Órdenes de Trabajo» del
 * menú apunta siempre a `/ordenes` (la lista). Un usuario en `/ordenes/calendario`
 * que se va a otro módulo y vuelve por ese enlace caía siempre en la lista,
 * perdiendo la vista de calendario en la que estaba — el enlace ahora lee esta
 * preferencia para decidir a cuál de las dos rutas apunta.
 */

const CLAVE_VISTA_OT = 'cmms:vista-ordenes';

export type VistaOrdenes = 'tabla' | 'calendario' | 'gantt';

export function obtenerVistaOrdenesPreferida(): VistaOrdenes {
  try {
    const valor = localStorage.getItem(CLAVE_VISTA_OT);
    return valor === 'calendario' || valor === 'gantt' ? valor : 'tabla';
  } catch {
    // Storage bloqueado (navegación privada, cuota) — la preferencia es solo
    // cosmética, así que se cae al valor por omisión sin romper nada.
    return 'tabla';
  }
}

export function guardarVistaOrdenesPreferida(vista: VistaOrdenes): void {
  try {
    localStorage.setItem(CLAVE_VISTA_OT, vista);
  } catch {
    // Igual que arriba: si falla, no pasa nada grave.
  }
}

/**
 * Tema visual (claro/oscuro), guardado igual que la vista de Órdenes: solo
 * cosmético y por navegador. Si el usuario nunca eligió, se sigue la
 * preferencia del sistema operativo.
 */
const CLAVE_TEMA = 'cmms:tema';

export type Tema = 'claro' | 'oscuro';

export function obtenerTemaPreferido(): Tema {
  try {
    const valor = localStorage.getItem(CLAVE_TEMA);
    if (valor === 'claro' || valor === 'oscuro') return valor;
  } catch {
    // Storage bloqueado: se cae a la preferencia del sistema.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'oscuro' : 'claro';
}

export function aplicarTema(tema: Tema): void {
  document.documentElement.dataset.tema = tema;
}

export function guardarTemaPreferido(tema: Tema): void {
  aplicarTema(tema);
  try {
    localStorage.setItem(CLAVE_TEMA, tema);
  } catch {
    // Igual que arriba: si falla, el tema aplica igual para esta sesión.
  }
}

/** Barra lateral de escritorio escondida o no. Solo cosmético, como el tema. */
const CLAVE_BARRA_OCULTA = 'cmms:barra-lateral-oculta';

export function obtenerBarraLateralOculta(): boolean {
  try {
    return localStorage.getItem(CLAVE_BARRA_OCULTA) === 'si';
  } catch {
    return false;
  }
}

export function guardarBarraLateralOculta(oculta: boolean): void {
  try {
    localStorage.setItem(CLAVE_BARRA_OCULTA, oculta ? 'si' : 'no');
  } catch {
    // Si no se puede guardar, la barra vuelve a mostrarse al recargar: nada grave.
  }
}

/** «Solo recurrentes» del Gantt: por defecto marcado, se recuerda al cambiarlo. */
const CLAVE_GANTT_SOLO_RECURRENTES = 'cmms:gantt-solo-recurrentes';

export function obtenerGanttSoloRecurrentes(): boolean {
  try {
    return localStorage.getItem(CLAVE_GANTT_SOLO_RECURRENTES) !== 'no';
  } catch {
    return true;
  }
}

export function guardarGanttSoloRecurrentes(activo: boolean): void {
  try {
    localStorage.setItem(CLAVE_GANTT_SOLO_RECURRENTES, activo ? 'si' : 'no');
  } catch {
    // Sin storage, el filtro vuelve a su valor por defecto al recargar.
  }
}
